import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Send, Facebook, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import { mockData } from '../data/mock';
import { supabase } from '../lib/supabase';

// Masquage visuel accessible (aucune classe .sr-only dans le CSS du projet)
const srOnly = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0
};

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('');
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    message: '',
    agency: ''  // Agence préférée
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      email: '',
      telephone: '',
      message: '',
      agency: ''
    });
    setConsent(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Piège à spam : seul un bot remplit ce champ caché → faux succès, aucun envoi
    if (e.target.website.value) {
      toast.success('Message envoyé !', {
        description: 'Nous vous recontacterons très rapidement.'
      });
      setStatus('Message envoyé. Nous vous recontacterons très rapidement.');
      resetForm();
      return;
    }

    setIsSubmitting(true);
    setStatus('');

    try {
      // Appel à Supabase
      const { error } = await supabase
        .from('contacts')
        .insert([formData]);

      // supabase-js résout même en cas d'erreur DB (RLS, contrainte...) : on la propage au catch
      if (error) throw error;

      toast.success('Message envoyé !', {
        description: 'Nous vous recontacterons très rapidement.'
      });
      setStatus('Message envoyé. Nous vous recontacterons très rapidement.');

      // Réinitialiser le formulaire
      resetForm();
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Erreur', {
        description: "Une erreur est survenue lors de l'envoi. Veuillez réessayer ou nous contacter par téléphone."
      });
      setStatus("Une erreur est survenue lors de l'envoi. Veuillez réessayer ou nous contacter par téléphone.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-contact">
      <div className="container">
        <div className="section-header" data-aos="fade-up">
          <h2 className="section-title">Contactez-nous</h2>
          <p className="section-subtitle">
            Parlons de votre projet de construction
          </p>
        </div>

        <div className="contact-wrapper">
          {/* Informations de contact */}
          <div className="contact-info" data-aos="fade-right" data-aos-delay="100">
            <h3 className="contact-info-title">Bouhdel</h3>
            <p className="contact-info-text">
              N'hésitez pas à nous contacter pour discuter de votre projet. 
              Notre secrétariat vous redirigera vers l'agence la plus proche de votre secteur.
            </p>

            <div className="contact-details">
              <div className="contact-detail-item">
                <Phone size={24} className="contact-icon" />
                <div>
                  <div className="contact-detail-label">Secrétariat</div>
                  <div className="contact-detail-value">04 90 47 15 78</div>
                </div>
              </div>

              <div className="contact-detail-item">
                <Mail size={24} className="contact-icon" />
                <div>
                  <div className="contact-detail-label">Email</div>
                  <div className="contact-detail-value">contact@bouhdel.com</div>
                </div>
              </div>
            </div>

            {/* Réseaux sociaux */}
            <div className="contact-social" style={{ marginTop: '2rem' }}>
              <p className="contact-detail-label" style={{ marginBottom: '1rem' }}>Suivez-nous</p>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <a 
                  href="https://www.facebook.com/p/Bouhdel-Constructeur-de-maisons-individuelles-100086627212377/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link"
                  aria-label="Facebook"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#70AD25',
                    color: 'white',
                    transition: 'transform 0.3s ease, background-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.backgroundColor = '#5a8d1e';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.backgroundColor = '#70AD25';
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.backgroundColor = '#5a8d1e';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.backgroundColor = '#70AD25';
                  }}
                >
                  <Facebook size={24} />
                </a>
                <a 
                  href="https://www.instagram.com/bouhdel_/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link"
                  aria-label="Instagram"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#70AD25',
                    color: 'white',
                    transition: 'transform 0.3s ease, background-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.backgroundColor = '#5a8d1e';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.backgroundColor = '#70AD25';
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.backgroundColor = '#5a8d1e';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.backgroundColor = '#70AD25';
                  }}
                >
                  <Instagram size={24} />
                </a>
              </div>
            </div>
          </div>

          {/* Formulaire de contact */}
          <div className="contact-form-wrapper" data-aos="fade-left" data-aos-delay="200">
            <form onSubmit={handleSubmit} className="contact-form" aria-describedby="form-required-note">
              {/* Piège à spam : invisible et hors tabulation pour les humains */}
              <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {/* Ligne 1: Nom + Email */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nom" className="form-label">Nom complet *</label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Votre nom"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              {/* Ligne 2: Téléphone + Agence */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="telephone" className="form-label">Téléphone</label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="06 XX XX XX XX"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="agency" className="form-label">Agence préférée</label>
                  <select
                    id="agency"
                    name="agency"
                    value={formData.agency}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">— Choisir une agence —</option>
                    {mockData.agencies.map(agency => (
                      <option key={agency.id} value={agency.name}>{agency.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Votre projet *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="form-textarea"
                  placeholder="Décrivez-nous brièvement votre projet..."
                ></textarea>
              </div>

              {/* Consentement RGPD explicite (CNIL : pas de consentement implicite) */}
              <div className="form-group" style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  id="consent"
                  name="consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  required
                  style={{ marginTop: '0.25rem', flexShrink: 0 }}
                />
                <span className="form-note" style={{ margin: 0 }}>
                  <label htmlFor="consent">J'accepte que mes données soient utilisées pour traiter ma demande</label>
                  {' '}(<Link to="/politique-confidentialite">politique de confidentialité</Link>) *
                </span>
              </div>

              <button 
                type="submit" 
                className="btn-primary btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                <Send size={20} style={{ marginLeft: '8px' }} />
              </button>

              {/* Retour d'envoi annoncé aux lecteurs d'écran */}
              <p role="status" aria-live="polite" style={srOnly}>{status}</p>

              <p className="form-note" id="form-required-note">
                * Champs obligatoires. Vos données sont protégées et ne seront jamais vendues ni cédées à des tiers à des fins commerciales.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
